import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { showToast } from '../utils/toast';
import { ExpertSession } from '../types';

export function useExpertSessions() {
  const [sessions, setSessions] = useState<ExpertSession[]>([]);
  const [userSessions, setUserSessions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    // Fetch all sessions regardless of authentication
    const fetchSessions = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('expert_sessions')
          .select(`
            id,
            expert_id,
            title,
            description,
            category,
            scheduled_date,
            duration,
            max_attendees,
            current_attendees,
            price,
            status,
            daily_room_url,
            recording_url,
            created_at,
            expert:profiles!expert_sessions_expert_id_fkey(
              id,
              full_name,
              avatar_url,
              is_expert
            )
          `)
          .order('scheduled_date', { ascending: true });

        if (error) throw error;

        // Format the sessions
        const formattedSessions: ExpertSession[] = data.map(session => ({
          id: session.id,
          expertId: session.expert_id,
          expertName: session.expert.full_name || 'Expert',
          title: session.title,
          description: session.description,
          category: session.category,
          scheduledDate: new Date(session.scheduled_date).toLocaleString(),
          duration: session.duration,
          maxAttendees: session.max_attendees,
          currentAttendees: session.current_attendees,
          price: session.price,
          status: session.status,
          attendees: [],
          recording: session.recording_url
        }));

        setSessions(formattedSessions);
      } catch (error) {
        console.error('Error fetching sessions:', error);
        showToast.system.error('Failed to load expert sessions');
      } finally {
        setLoading(false);
      }
    };

    // Fetch user's registered sessions if authenticated
    const fetchUserSessions = async () => {
      if (!user) {
        setUserSessions([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('session_attendees')
          .select('session_id')
          .eq('user_id', user.id);

        if (error) throw error;

        setUserSessions(data.map(item => item.session_id));
      } catch (error) {
        console.error('Error fetching user sessions:', error);
      }
    };

    fetchSessions();
    fetchUserSessions();

    // Subscribe to session updates
    const sessionSubscription = supabase
      .channel('expert_sessions_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'expert_sessions'
      }, async (payload) => {
        // Refetch all sessions when there's a change
        await fetchSessions();
      })
      .subscribe();

    // Subscribe to user's session registrations if authenticated
    let attendeeSubscription: any = null;
    if (user) {
      attendeeSubscription = supabase
        .channel('session_attendees_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'session_attendees',
          filter: `user_id=eq.${user.id}`
        }, async (payload) => {
          await fetchUserSessions();
        })
        .subscribe();
    }

    // Cleanup subscriptions
    return () => {
      supabase.removeChannel(sessionSubscription);
      if (attendeeSubscription) {
        supabase.removeChannel(attendeeSubscription);
      }
    };
  }, [user]);

  const reserveSession = async (sessionId: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to reserve a session');
      throw new Error('Not authenticated');
    }

    try {
      // Check if already registered
      if (userSessions.includes(sessionId)) {
        showToast.system.info('You are already registered for this session');
        return;
      }

      // Register for the session
      const { error } = await supabase
        .from('session_attendees')
        .insert({
          session_id: sessionId,
          user_id: user.id,
          reminder_set: true
        });

      if (error) throw error;

      // Update current attendees count
      const { error: updateError } = await supabase
        .from('expert_sessions')
        .update({
          current_attendees: supabase.rpc('increment', { row_id: sessionId, table: 'expert_sessions', column: 'current_attendees', amount: 1 })
        })
        .eq('id', sessionId);

      if (updateError) throw updateError;

      // Update local state
      setUserSessions(prev => [...prev, sessionId]);
      
      // Find the session to show in the toast
      const session = sessions.find(s => s.id === sessionId);
      if (session) {
        showToast.expert.sessionJoined(session.title);
      }
    } catch (error) {
      console.error('Error reserving session:', error);
      showToast.system.error('Failed to reserve session');
      throw error;
    }
  };

  const cancelReservation = async (sessionId: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to cancel a reservation');
      throw new Error('Not authenticated');
    }

    try {
      // Delete the registration
      const { error } = await supabase
        .from('session_attendees')
        .delete()
        .eq('session_id', sessionId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update current attendees count
      const { error: updateError } = await supabase
        .from('expert_sessions')
        .update({
          current_attendees: supabase.rpc('decrement', { row_id: sessionId, table: 'expert_sessions', column: 'current_attendees', amount: 1 })
        })
        .eq('id', sessionId);

      if (updateError) throw updateError;

      // Update local state
      setUserSessions(prev => prev.filter(id => id !== sessionId));
      
      showToast.system.success('Session reservation cancelled');
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      showToast.system.error('Failed to cancel reservation');
      throw error;
    }
  };

  const isSessionReserved = (sessionId: string) => {
    return userSessions.includes(sessionId);
  };

  return {
    sessions,
    loading,
    reserveSession,
    cancelReservation,
    isSessionReserved
  };
}